import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Target, GraduationCap } from 'lucide-react';
const PHASES = [{
  id: 'B',
  label: 'Begin',
  months: 'Sept – Nov',
  desc: 'Basisvaardigheden & herhaling vorig jaar',
  color: 'sky'
}, {
  id: 'M',
  label: 'Midden',
  months: 'Dec – Feb',
  desc: 'Verdieping & nieuwe onderwerpen',
  color: 'violet'
}, {
  id: 'E',
  label: 'Eind',
  months: 'Mrt – Jul',
  desc: 'Uitbreiding & voorbereiding volgend jaar',
  color: 'orange'
}];
const GROUPS = [{
  group: 6,
  label: 'Groep 6',
  tiers: 'Tier 1 – 8',
  range: [0, 50],
  color: 'bg-emerald-500'
}, {
  group: 7,
  label: 'Groep 7',
  tiers: 'Tier 5 – 12',
  range: [25, 75],
  color: 'bg-orange-500'
}, {
  group: 8,
  label: 'Groep 8',
  tiers: 'Tier 9 – 16',
  range: [50, 100],
  color: 'bg-rose-500'
}];
const COLOR_MAP: Record<string, {
  bg: string;
  text: string;
  ring: string;
  solid: string;
}> = {
  sky: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    ring: 'ring-sky-200',
    solid: 'bg-sky-500'
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    ring: 'ring-violet-200',
    solid: 'bg-violet-500'
  },
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    solid: 'bg-orange-500'
  }
};
export function CurriculumAlignmentSection() {
  const [activePhase, setActivePhase] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActivePhase(p => (p + 1) % 3), 2400);
    return () => clearInterval(t);
  }, []);
  return <section id="curriculum" className="relative py-24 lg:py-32 bg-white overflow-hidden" data-id="element-48">
      <div className="absolute inset-0 pointer-events-none" data-id="element-49">
        <div className="absolute top-10 right-10 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl" data-id="element-50" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-51">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-52">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider mb-6" data-id="element-53">
            <GraduationCap className="w-3.5 h-3.5" data-id="element-54" />
            Aansluiting op school
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight" data-id="element-55">
            Past zich aan op{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-56">
              schooljaar & groep
            </span>
          </h2>
          <p className="text-lg text-slate-600 font-medium" data-id="element-57">
            Meester djay weet niet alleen wat je kind kan — ook waar in het
            schooljaar je zit en in welke groep. Zo sluit elke oefening aan op
            wat er nú op school gebeurt.
          </p>
        </div>

        {/* B/M/E timeline */}
        <div className="mb-16" data-id="element-58">
          <div className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-wider mb-4" data-id="element-59">
            <Calendar className="w-4 h-4" data-id="element-60" />
            Begin · Midden · Eind van het schooljaar
          </div>

          <div className="relative bg-gradient-to-r from-sky-50 via-violet-50 to-orange-50 rounded-2xl p-2 border border-slate-200" data-id="element-61">
            <div className="grid grid-cols-3 gap-2 relative" data-id="element-62">
              {PHASES.map((phase, i) => {
              const c = COLOR_MAP[phase.color];
              const isActive = i === activePhase;
              return <motion.div key={phase.id} animate={{
                scale: isActive ? 1.02 : 1
              }} className={`relative rounded-xl p-5 transition-all ${isActive ? `bg-white shadow-lg ring-2 ${c.ring}` : 'bg-white/40'}`} data-id="element-63">
                    <div className="flex items-center justify-between mb-2" data-id="element-64">
                      <div className={`w-9 h-9 rounded-xl ${c.solid} text-white flex items-center justify-center text-base font-black shadow-sm`} data-id="element-65">
                        {phase.id}
                      </div>
                      {isActive && <motion.div initial={{
                    opacity: 0,
                    x: -5
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} className={`text-[10px] font-black uppercase tracking-wider ${c.text} ${c.bg} px-2 py-1 rounded-full flex items-center gap-1`} data-id="element-66">
                          <motion.div animate={{
                      scale: [1, 1.4, 1]
                    }} transition={{
                      duration: 1.2,
                      repeat: Infinity
                    }} className={`w-1.5 h-1.5 rounded-full ${c.solid}`} data-id="element-67" />
                          NU
                        </motion.div>}
                    </div>
                    <div className={`text-lg font-black ${isActive ? c.text : 'text-slate-700'}`} data-id="element-68">
                      {phase.label}
                    </div>
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-69">
                      {phase.months}
                    </div>
                    <div className="text-xs text-slate-600 font-medium leading-snug" data-id="element-70">
                      {phase.desc}
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>

        {/* Tier windowing per group */}
        <div data-id="element-71">
          <div className="flex items-center gap-2 text-xs font-black text-slate-500 uppercase tracking-wider mb-4" data-id="element-72">
            <Target className="w-4 h-4" data-id="element-73" />
            Niveau per groep — zoete plek, geen frustratie
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-id="element-74">
            <div className="space-y-5" data-id="element-75">
              {/* Tier scale */}
              <div className="relative pt-2" data-id="element-76">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-77">
                  <span data-id="element-78">Makkelijk</span>
                  <span data-id="element-79">16 niveaus</span>
                  <span data-id="element-80">Moeilijk</span>
                </div>
                <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-81">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-200 via-amber-200 to-rose-200" data-id="element-82" />
                  {Array.from({
                  length: 17
                }).map((_, i) => <div key={i} className="absolute top-0 bottom-0 w-px bg-white/60" style={{
                  left: `${i / 16 * 100}%`
                }} data-id="element-83" />)}
                </div>
              </div>

              {/* Group bands */}
              {GROUPS.map((g, i) => <motion.div key={g.group} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.15
            }} className="relative" data-id="element-84">
                  <div className="flex items-center justify-between mb-1.5" data-id="element-85">
                    <div className="flex items-center gap-2" data-id="element-86">
                      <div className={`w-8 h-8 rounded-xl ${g.color} text-white flex items-center justify-center text-sm font-black shadow-sm`} data-id="element-87">
                        {g.group}
                      </div>
                      <span className="text-sm font-black text-slate-800" data-id="element-88">
                        {g.label}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-500 font-mono" data-id="element-89">
                      {g.tiers}
                    </span>
                  </div>
                  <div className="relative h-9 bg-slate-50 rounded-xl overflow-hidden" data-id="element-90">
                    <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: `${g.range[1] - g.range[0]}%`
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.3 + i * 0.15,
                  duration: 0.8,
                  ease: 'easeOut'
                }} className={`absolute top-0 bottom-0 ${g.color} rounded-xl flex items-center justify-center text-[10px] font-black text-white uppercase tracking-wider shadow-sm`} style={{
                  left: `${g.range[0]}%`
                }} data-id="element-91">
                      Sweet spot
                    </motion.div>
                  </div>
                </motion.div>)}
            </div>

            <p className="text-xs font-bold text-slate-500 mt-6 leading-relaxed" data-id="element-92">
              Een groep 6-leerling krijgt geen vraag van het hoogste niveau. Een
              talent in groep 8 krijgt wél de uitdaging die past. De overlap
              zorgt dat kinderen kunnen doorgroeien zonder vast te lopen op de
              grens van hun jaar.
            </p>
          </div>
        </div>
      </div>
    </section>;
}